import List from '@components/v2/ui/List/List';
import { Theme } from '@mui/material';
import Box from '@mui/material/Box';
import { memo, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Option } from '../../../../Crud/filters/components/MultiOptionsSelectorFilter/components/MultiOptionsSelector/components/OptionsList/optionsList.types';
import { compareOptionWithQuery } from '../../hooks/useDynamicOptionsSelector';
import useOptionsList from '../../hooks/useMultiOptionsList';
import EmptyList from './EmptyList';
import LoadingOptions from './LoadingOptions';
import OptionComponent from './components/Option/Option';
import OptionsListProps from './optionsList.types';

const useStyles = makeStyles()((_theme: Theme) => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  selectedOption: {
    order: -1,
  },
}));

const OptionsList = ({
  options,
  selectedValues,
  onSelect,
  isLoadingOptions,
  query,
  isRecursiveList,
  canReorder = true,
  isChildren,
  fillHeight,
}: OptionsListProps) => {
  const { classes, cx } = useStyles();

  const verifyIfOptionIsVisible = useCallback(
    (option: Option) => compareOptionWithQuery(option, query),
    [query],
  );

  const {
    handleGetParamsToOption,
    handleSelectChildren,
    isVisible,
    validateIfCanAddSelectedClass,
  } = useOptionsList({
    options,
    canReorder,
    onSelect,
    selectedValues,
    verifyIfOptionIsVisible: verifyIfOptionIsVisible,
  });

  const hasVisibleOptions = options.some((option) => isVisible(option));

  const list = (
    <List className={classes.list}>
      {options.map((option) => {
        if (!isVisible(option)) {
          return null;
        }

        const hasChildren = !!option.children?.length;
        const key = `${hasChildren ? 'aggregator-list-value-' : 'checkbox-list-value-'}${
          option.value
        }`;

        return (
          <div
            key={key}
            className={cx({
              [classes.selectedOption]: validateIfCanAddSelectedClass(option),
            })}
          >
            <OptionComponent
              label={option.label}
              value={option.value}
              isVisible={isVisible(option)}
              params={handleGetParamsToOption(option)}
              isChildren={isChildren}
            />
            {hasChildren && (
              <OptionsList
                canReorder={false}
                isRecursiveList
                onSelect={handleSelectChildren}
                options={option.children!}
                selectedValues={selectedValues}
                query={query}
                isChildren
              />
            )}
          </div>
        );
      })}
    </List>
  );

  if (isRecursiveList) {
    return list;
  }

  return (
    <Box sx={{ paddingRight: fillHeight ? 1 : 0, paddingBottom: fillHeight ? 1 : 0 }}>
      {list}
      <EmptyList isLoading={!!isLoadingOptions} hasData={hasVisibleOptions} query={query} />
      <LoadingOptions isLoading={!!isLoadingOptions} hasData={options.length > 0} />
    </Box>
  );
};

export default memo(OptionsList);
